import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import Card, { CardHeader } from '../components/Card';
import DataTable, { Column } from '../components/DataTable';
import Badge from '../components/Badge';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import SearchInput from '../components/SearchInput';
import Toast, { ToastMessage } from '../components/Toast';
import axios from 'axios';
import { BaseUrl } from '../services/adminApi';

interface SubscriptionRow {
  id: number;
  subscriber_name: string;
  subscriber_email: string;
  plan_name: string;
  status: 'active' | 'pending' | 'cancelled' | 'expired' | string;
  payment_status: string;
  start_date: string | null;
  end_date: string | null;
  amount: number;
  currency_symbol: string;
}

const PAGE_SIZE = 10;
const DURATIONS = [
  { label: '1 month', months: 1 },
  { label: '3 months', months: 3 },
  { label: '6 months', months: 6 },
  { label: '12 months', months: 12 },
];

const FALLBACK_LAPSED: SubscriptionRow[] = [
  { id: 101, subscriber_name: 'Kavya Raman', subscriber_email: 'kavya@example.com', plan_name: 'Starter', status: 'expired', payment_status: 'Paid', start_date: '2025-06-10', end_date: '2026-06-10', amount: 499, currency_symbol: '₹' },
  { id: 102, subscriber_name: 'Global Press Ltd', subscriber_email: 'contact@globalpress.example', plan_name: 'Publisher Pro', status: 'expired', payment_status: 'Paid', start_date: '2025-05-01', end_date: '2026-06-28', amount: 9999, currency_symbol: '₹' },
  { id: 103, subscriber_name: 'Arjun Mehta', subscriber_email: 'arjun@example.com', plan_name: 'Professional', status: 'expired', payment_status: 'Pending', start_date: '2025-07-15', end_date: '2026-07-01', amount: 1499, currency_symbol: '₹' },
];

function formatDate(iso: string | null) {
  if (!iso || iso === '—') return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatAmount(row: SubscriptionRow) {
  return `${row.currency_symbol}${Number(row.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function daysLapsed(iso: string | null): number | null {
  if (!iso || iso === '—') return null;
  const diff = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}

/**
 * A row counts as "lapsed" either because the backend already flagged it
 * `expired`, or because it's still marked `active` but its end date has
 * already passed (mirrors the effectiveStatus logic used on the
 * Subscriptions page, so the two pages never disagree on who's lapsed).
 */
function isLapsed(row: SubscriptionRow): boolean {
  if (row.status === 'expired') return true;
  if (row.status === 'active') {
    const d = daysLapsed(row.end_date);
    return d !== null && new Date(row.end_date as string).getTime() < Date.now();
  }
  return false;
}

export default function Lapsed() {
  const [subs, setSubs] = useState<SubscriptionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [renewTarget, setRenewTarget] = useState<SubscriptionRow | null>(null);
  const [renewMonths, setRenewMonths] = useState(1);
  const [renewing, setRenewing] = useState(false);
  const [selected, setSelected] = useState<SubscriptionRow | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const showToast = (text: string, type: ToastMessage['type'] = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    setPage(1);
        setSubs(FALLBACK_LAPSED);
  }, [search,FALLBACK_LAPSED]);

  // Real endpoint: GET /admin/subscriptions/?filter=expired&search=&pageNum=&pageSize=
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await axios.get(BaseUrl + 'admin/subscriptions/', {
          params: { filter: 'expired', search, pageNum: page, pageSize: PAGE_SIZE },
        });
        if (cancelled) return;
        const data = res.data;
        const results: SubscriptionRow[] = Array.isArray(data) ? data : (data?.results ?? []);
        const count = Array.isArray(data) ? results.length : (data?.count ?? results.length);
        setSubs(results);
        setTotalCount(count);
        setTotalPages(Math.max(1, Math.ceil(count / PAGE_SIZE)));
      } catch {
        if (cancelled) return;
        const fallback = FALLBACK_LAPSED.filter(s =>
          !search || s.subscriber_name.toLowerCase().includes(search.toLowerCase()) || s.subscriber_email.toLowerCase().includes(search.toLowerCase())
        );
        setSubs(fallback);
        setTotalCount(fallback.length);
        setTotalPages(1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [page, search]);

  const rows = useMemo(() => subs.filter(isLapsed), [subs]);

  const stats = useMemo(() => {
    const c = { count: rows.length, revenueAtRisk: 0, avgDaysLapsed: 0, over30: 0 };
    let daysSum = 0, daysCount = 0;
    rows.forEach(r => {
      c.revenueAtRisk += Number(r.amount) || 0;
      const d = daysLapsed(r.end_date);
      if (d !== null) { daysSum += d; daysCount += 1; if (d > 30) c.over30 += 1; }
    });
    c.avgDaysLapsed = daysCount ? Math.round(daysSum / daysCount) : 0;
    return c;
  }, [rows]);

  const openRenew = (row: SubscriptionRow) => {
    setRenewTarget(row);
    setRenewMonths(1);
  };

  const confirmRenew = async () => {
    if (!renewTarget) return;
    setRenewing(true);
    const newDate = new Date();
    newDate.setMonth(newDate.getMonth() + renewMonths);
    const newDateIso = newDate.toISOString().slice(0, 10);

    try {
      await axios.post(BaseUrl + `admin/subscriptions/${renewTarget.id}/renew/`, { months: renewMonths });
    } catch {
      // Backend renew endpoint may not exist yet — keep the panel usable.
    }
    // Renewed subscribers move off the Lapsed list, back onto Subscriptions/Renewals.
    setSubs(prev => prev.filter(s => s.id !== renewTarget.id));
    setTotalCount(c => Math.max(0, c - 1));
    showToast(`${renewTarget.subscriber_name}'s subscription renewed to ${formatDate(newDateIso)}.`);
    setRenewing(false);
    setRenewTarget(null);
  };

  const columns: Column<SubscriptionRow>[] = [
    {
      key: 'user', header: 'Subscriber', render: r => (
        <div>
          <p className="font-bold text-gray-900">{r.subscriber_name}</p>
          {r.subscriber_email && <p className="text-[11px] text-gray-400 font-medium">{r.subscriber_email}</p>}
        </div>
      )
    },
    { key: 'plan', header: 'Plan', render: r => r.plan_name },
    { key: 'end_date', header: 'Lapsed On', render: r => formatDate(r.end_date) },
    {
      key: 'days_lapsed', header: 'Days Lapsed', render: r => {
        const d = daysLapsed(r.end_date);
        return <Badge tone={d !== null && d > 30 ? 'red' : 'amber'}>{d === null ? '—' : `${d}d`}</Badge>;
      }
    },
    {
      key: 'payment_status', header: 'Payment', render: r => (
        <Badge tone={r.payment_status?.toLowerCase() === 'paid' ? 'green' : 'amber'}>{r.payment_status || '—'}</Badge>
      )
    },
    { key: 'amount', header: 'Amount', render: r => formatAmount(r), align: 'right' },
    {
      key: 'actions', header: '', align: 'right', render: r => (
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={e => { e.stopPropagation(); setSelected(r); }}
            className="text-[12px] font-bold text-gray-500 hover:underline"
          >
            View
          </button>
          <button
            onClick={e => { e.stopPropagation(); openRenew(r); }}
            className="text-[12px] font-bold text-navy hover:underline"
          >
            Renew
          </button>
        </div>
      )
    },
  ];

  return (
    <div>
      <PageHeader title="Lapsed Subscribers" subtitle="Subscribers whose plan has expired without renewal — follow up or renew from here." />

      {/* <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <StatCard label="Lapsed (this page)" value={stats.count} icon="ti-user-x" iconBg="#fef2f2" iconColor="#dc2626" />
        <StatCard label="Lapsed &gt; 30 Days" value={stats.over30} icon="ti-calendar-exclamation" iconBg="#fffbeb" iconColor="#d97706" />
        <StatCard label="Avg. Days Lapsed" value={`${stats.avgDaysLapsed}d`} icon="ti-clock-pause" iconBg="#eff6ff" iconColor="#2563eb" />
        <StatCard label="Revenue at Risk" value={`₹${stats.revenueAtRisk.toLocaleString('en-IN')}`} icon="ti-currency-rupee" iconBg="#f5f3ff" iconColor="#7c3aed" />
      </div> */}

      <Card>
        <CardHeader
          title="Lapsed subscribers"
          subtitle="Subscriptions that expired and haven't been renewed yet"
          action={<SearchInput value={search} onChange={setSearch} placeholder="Search subscriber, plan, email…" className="w-64" />}
        />
        <DataTable
          columns={columns}
          rows={subs}
          loading={loading}
          onRowClick={setSelected}
          emptyTitle="No lapsed subscribers"
          emptySubtitle="Everyone is currently active or already renewed. Nice work!"
        />
        <Pagination page={page} totalPages={totalPages} onChange={setPage} totalItems={totalCount} pageSize={PAGE_SIZE} />
      </Card>

      {/* Details modal */}
      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title="Lapsed subscription details"
        footer={selected && (
          <>
            <Button variant="secondary" size="sm" onClick={() => setSelected(null)}>Close</Button>
            <Button
              variant="primary"
              size="sm"
              icon="ti-refresh"
              onClick={() => { openRenew(selected); setSelected(null); }}
            >
              Renew
            </Button>
          </>
        )}
      >
        {selected && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-navy text-white flex items-center justify-center font-black text-[14px]">
                {selected.subscriber_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <p className="font-black text-gray-900 text-[14px]">{selected.subscriber_name}</p>
                <p className="text-[12px] text-gray-500">{selected.subscriber_email}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              {[
                { label: 'Plan', value: selected.plan_name },
                { label: 'Payment', value: <Badge tone={selected.payment_status?.toLowerCase() === 'paid' ? 'green' : 'amber'}>{selected.payment_status || '—'}</Badge> },
                { label: 'Start date', value: formatDate(selected.start_date) },
                { label: 'Lapsed on', value: formatDate(selected.end_date) },
                { label: 'Days lapsed', value: `${daysLapsed(selected.end_date) ?? '—'}d` },
                { label: 'Amount', value: formatAmount(selected) },
              ].map(f => (
                <div key={f.label}>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">{f.label}</p>
                  <div className="text-[13px] font-semibold text-gray-800">{f.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* Renew modal — same flow as Subscriptions & Renewals page */}
      <Modal
        open={!!renewTarget}
        onClose={() => !renewing && setRenewTarget(null)}
        title="Renew subscription"
        width={420}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setRenewTarget(null)} disabled={renewing}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={confirmRenew} loading={renewing} icon="ti-refresh">
              Confirm renewal
            </Button>
          </>
        }
      >
        {renewTarget && (
          <div>
            <p className="text-[13px] text-gray-600 font-medium leading-relaxed mb-4">
              Renewing <span className="font-bold text-gray-900">{renewTarget.subscriber_name}</span>'s{' '}
              <span className="font-bold text-gray-900">{renewTarget.plan_name}</span> plan
              {renewTarget.end_date && <> (lapsed since {formatDate(renewTarget.end_date)})</>}.
            </p>
            <label className="text-[12px] font-bold text-gray-600 mb-1.5 block">Extend by</label>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {DURATIONS.map(d => (
                <button
                  key={d.months}
                  onClick={() => setRenewMonths(d.months)}
                  className={`text-[12px] font-bold py-2 rounded-lg border transition-colors ${renewMonths === d.months
                      ? 'bg-navy text-white border-navy'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 flex items-center justify-between">
              <span className="text-[12px] font-bold text-gray-500">New renewal date</span>
              <span className="text-[13px] font-black text-gray-900">
                {formatDate(
                  (() => {
                    const d = new Date();
                    d.setMonth(d.getMonth() + renewMonths);
                    return d.toISOString().slice(0, 10);
                  })()
                )}
              </span>
            </div>
          </div>
        )}
      </Modal>

      <Toast message={toast} />
    </div>
  );
}
